import React, { useEffect, useRef, useState } from "react";
import {
  StatusBar,
  StyleSheet,
  TouchableOpacity,
  View,
  Text,
  Pressable,
  BackHandler,
} from "react-native";
import dayjs from "dayjs";
import { useFocusEffect } from "@react-navigation/native";
import { useDispatch, useSelector } from "react-redux";

import BackArrow from "@assets/svg/BackArrow";
import colors from "@constants/color";
import metrics from "@constants/metrics";
import { scale } from "@utils/scale";
import Caret from "@assets/svg/CaretIcon";
import t from "@constants/localization";
import analytics from "@services/analytics";
import { SHOP_PERFORMANCE_EVENTS } from "@constants/analyticsEventsCT";
import testIds from "@constants/testIds";
import { getSales, getLastMonthsOverview } from "@services/api";
import { actions } from "@reducers/shopPerformanceReducer";
import Card from "./Card";
import Loader from "./LoaderScreens/Loader";
import SalesModal from "./Components/SalesModal";
import TabViewComponent from "./Components/TabViewComponent";

const PATH = "screens/ShopPerformance/index";

const ShopPerformance = (props) => {
  const { navigation } = props;
  const [visible, setVisible] = useState(false);
  const [isLoading, setLoading] = useState(true);
  const [showSaleCard, setSaleCard] = useState(false);
  const [showCommissionCard, setCommissionCard] = useState(false);
  const { selectedMonth } = useSelector(
    ({ shopPerformance }) => shopPerformance,
  );
  const currentMonthName = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const month = `${
    currentMonthName[parseInt(selectedMonth.substring(5, 7)) - 1]
  }, ${selectedMonth.substring(0, 4)}`;
  const currMonth = `${dayjs(new Date()).format("YYYY-MM")}-01`;
  const backHandler = useRef();
  const dispatch = useDispatch();

  const handleMonthView = () => {
    setSaleCard(false);
    setCommissionCard(false);
    setVisible(!visible);
    analytics.trackEvent(SHOP_PERFORMANCE_EVENTS.MONTH_SELECTED_CLICKED, {
      Old_Selected_Month: month,
    });
  };

  useEffect(() => {
    fetchLastMonthsOverview();
  }, []);

  useEffect(() => {
    fetchSales();
  }, [selectedMonth]);

  const fetchLastMonthsOverview = async () => {
    try {
      const { data } = await getLastMonthsOverview(currMonth);
      dispatch(
        actions.setLastMonthOverview({
          data,
        }),
      );
    } catch (error) {
      console.log("Error : ", error);
    }
  };
  const fetchSales = async () => {
    try {
      setLoading(true);
      const { data } = await getSales(selectedMonth);
      dispatch(
        actions.updateCustomerDetails({
          data: data.customerDetails,
        }),
      );
      dispatch(
        actions.updateProductDetails({
          data: data.productDetails,
        }),
      );

      dispatch(
        actions.updateItemDetails({
          data: data.skuDetails,
        }),
      );

      dispatch(
        actions.updateSales({
          data: data.salesOverview,
        }),
      );

      const commissions = {
        totalCommission: 0,
        bonus: 0,
        processed: 0,
        processing: 0,
      };
      data.commissionsOverview.map((item) => {
        if (item.type === "commission-credit" && item.status == "processing") {
          commissions.totalCommission += parseInt(item.total_commission);
          commissions.processing = parseInt(item.total_commission);
        } else if (
          item.type === "commission-credit" &&
          item.status === "processed"
        ) {
          commissions.processed = parseInt(item.total_commission);
          commissions.totalCommission += parseInt(item.total_commission);
        } else if (
          item.type === "bonus-commission-credit" &&
          item.status === "processed"
        ) {
          commissions.bonus += parseInt(item.total_commission);
        }
      });
      dispatch(
        actions.updateCommission({
          data: commissions,
        }),
      );

      setLoading(false);
    } catch (error) {
      console.log("Error : ", error);
      setLoading(false);
    }
  };

  const handleCardVisibility = () => {
    if (showSaleCard) setSaleCard(false);
    if (showCommissionCard) setCommissionCard(false);
  };

  useFocusEffect(
    React.useCallback(() => {
      backHandler.current = BackHandler.addEventListener(
        "hardwareBackPress",
        handleBackPress,
      );
      return () => backHandler.current.remove();
    }, []),
  );
  const handleBackPress = () => {
    navigation.goBack();
    return true;
  };
  if (isLoading) {
    return (
      <View style={styles.loader}>
        <Loader />
      </View>
    );
  }
  return (
    <Pressable
      style={{ flex: 1 }}
      onPress={() => {
        handleCardVisibility();
      }}
    >
      <View style={{ flex: 1 }}>
        <>
          <StatusBar animated={true} barStyle="dark-content" />
          <View style={styles.topNavContainer}>
            <View style={styles.topNav}>
              <TouchableOpacity
                onPress={() => {
                  navigation.goBack();
                }}
                style={styles.arrowCont}
              >
                <BackArrow color={colors.white} />
              </TouchableOpacity>
              <View style={styles.header}>
                <View>
                  <Text style={styles.textLeftPadding}>
                    {t(`${PATH}/shopPerformance`)}
                  </Text>
                </View>
                <TouchableOpacity
                  style={styles.monthView}
                  onPress={() => {
                    handleMonthView();
                  }}
                >
                  <Text style={styles.text}>{month}</Text>
                  <View
                    style={styles.caretButton}
                    testID={testIds.month_selected}
                  >
                    <Caret strokeColor={colors.white} />

                    <SalesModal
                      visible={visible}
                      setVisible={setVisible}
                      month={month}
                    />
                  </View>
                </TouchableOpacity>
              </View>
            </View>
          </View>

          <Card
            showSaleCard={showSaleCard}
            setSaleCard={setSaleCard}
            showCommissionCard={showCommissionCard}
            setCommissionCard={setCommissionCard}
          />
          <TabViewComponent
            setSaleCard={setSaleCard}
            setCommissionCard={setCommissionCard}
            {...props}
          />
        </>
      </View>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  topNavContainer: {
    flexDirection: "row",
    height: scale(60),
    paddingHorizontal: metrics.mainContainerPadding,
    backgroundColor: "#8691F4",
    alignItems: "center",
  },
  topNav: { flexDirection: "row", alignItems: "center" },
  textLeftPadding: {
    paddingLeft: scale(25),
    color: colors.white,
    fontSize: scale(20),
    lineHeight: scale(30),
    fontFamily: "NotoSansDevanagari-Bold",
  },
  loader: { flex: 1, justifyContent: "center", alignItems: "center" },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "93.5%",
    alignItems: "center",
  },
  caretButton: {
    marginLeft: 8,
    marginTop: 3,
    alignContent: "center",
  },
  monthView: {
    flexDirection: "row",
    alignItems: "center",
  },
  text: {
    color: colors.white,
    fontSize: scale(16),
    marginTop: scale(4),
    lineHeight: scale(24),
    fontFamily: "NotoSansDevanagari-Bold",
  },
});

export default ShopPerformance;
